import * as publicEnv from "$env/static/public";
import { browser } from "$app/environment";
import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import * as firebaseAnalytics from "firebase/analytics";
import type { Analytics } from "firebase/analytics";
import { privacyConsent, getPrivacyConsentRecord, type ConsentChoice } from "$lib/privacy-consent";

export type AnalyticsConsent = ConsentChoice;

type EventParams = Record<string, unknown>;

const AUTH_FAILURE_FLOWS = ["sign_in", "guest_upgrade"] as const;
const AUTH_FAILURE_METHODS = ["anonymous", "google", "microsoft", "password", "unknown"] as const;
const AUTH_FAILURE_STAGES = [
  "persistence",
  "anonymous",
  "popup",
  "redirect",
  "credential",
  "account_link",
  "completion",
] as const;
const AUTH_FAILURE_REASONS = [
  "popup_closed",
  "popup_cancelled",
  "popup_blocked",
  "network_request_failed",
  "credential_collision",
  "account_exists_with_different_credential",
  "email_already_in_use",
  "operation_not_allowed",
  "too_many_requests",
  "invalid_credential",
  "wrong_password",
  "user_not_found",
  "invalid_email",
  "redirect_failed",
  "redirect_no_result",
  "uid_mismatch",
  "account_link_failed",
  "unknown",
] as const;
const RETURNING_START_VARIANTS = ["returning_progress", "returning_personalised", "returning_generic"] as const;
const RETURNING_START_RECOMMENDATION_FAMILIES = [
  "action_plan",
  "recent_activity",
  "explicit_preference",
  "profile_interest",
  "completed_action",
  "find_next_step",
] as const;
const RETURNING_START_URGENCY_BANDS = ["overdue", "today", "next_7_days", "undated", "later"] as const;
const RETURNING_START_DESTINATION_FAMILIES = [
  "action_plan",
  "profiles",
  "jobs",
  "training",
  "cv",
  "interview",
  "find_next_step",
  "coach",
] as const;
const RETURNING_START_POSITIONS = ["primary", "secondary", "fallback"] as const;

let analyticsInstance: Analytics | null = null;
let initializationAttempted = false;
let appliedConsent: AnalyticsConsent | null = null;

const { getAnalytics, initializeAnalytics, logEvent } = firebaseAnalytics;
const setFirebaseAnalyticsConsent = (
  firebaseAnalytics as typeof firebaseAnalytics & {
    setConsent?: (consent: { analytics_storage: AnalyticsConsent }) => void;
  }
).setConsent;

const readPublicEnv = (staticValue: string | undefined, key: string) => {
  if (String(staticValue || "").trim()) {
    return String(staticValue).trim();
  }

  if (browser) {
    const viteEnv = import.meta.env as Record<string, string | undefined>;
    return String(viteEnv[key] || "").trim();
  }

  return String(process.env[key] || "").trim();
};

const analyticsEnabled = () =>
  readPublicEnv(publicEnv.PUBLIC_ANALYTICS_ENABLED, "PUBLIC_ANALYTICS_ENABLED").toLowerCase() === "true";

const analyticsDebugEnabled = () =>
  readPublicEnv(publicEnv.PUBLIC_ANALYTICS_DEBUG, "PUBLIC_ANALYTICS_DEBUG").toLowerCase() === "true";

const allowedOrigin = () => readPublicEnv(publicEnv.PUBLIC_ANALYTICS_ALLOWED_ORIGIN, "PUBLIC_ANALYTICS_ALLOWED_ORIGIN");

const measurementId = () => readPublicEnv(publicEnv.PUBLIC_FIREBASE_MEASUREMENT_ID, "PUBLIC_FIREBASE_MEASUREMENT_ID");

const debugLog = (message: string, details?: unknown) => {
  if (analyticsDebugEnabled()) {
    console.info(`[analytics] ${message}`, details ?? "");
  }
};

const isAnalyticsCookie = (name: string) =>
  name === "_ga" || name === "_gid" || name === "_gat" || /^_(?:ga|gat|gac)_.+/.test(name);

export function deleteAccessibleAnalyticsCookies() {
  if (!browser) return;

  const names = document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter((name) => name && isAnalyticsCookie(name));
  const hostname = window.location.hostname;
  const domains = [hostname];
  if (hostname === "getzero.earth" || hostname.endsWith(".getzero.earth")) {
    domains.push(".getzero.earth");
  }

  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}; SameSite=Lax`;
    }
  }
}

function applyAnalyticsConsent(consent: AnalyticsConsent) {
  if (!browser || appliedConsent === consent) return;
  appliedConsent = consent;
  try {
    setFirebaseAnalyticsConsent?.({
      analytics_storage: consent,
    });
  } catch (error) {
    debugLog("Firebase analytics consent update failed", error);
  }

  if (consent === "denied") {
    analyticsInstance = null;
    initializationAttempted = false;
    deleteAccessibleAnalyticsCookies();
    return;
  }

  initAnalyticsIfAllowed();
}

privacyConsent.subscribe(({ initialized, record }) => {
  if (initialized) applyAnalyticsConsent(record?.analytics ?? "denied");
});

const firebaseConfig = () => ({
  apiKey: readPublicEnv(publicEnv.PUBLIC_FIREBASE_API_KEY, "PUBLIC_FIREBASE_API_KEY"),
  authDomain: readPublicEnv(publicEnv.PUBLIC_FIREBASE_AUTH_DOMAIN, "PUBLIC_FIREBASE_AUTH_DOMAIN"),
  projectId: readPublicEnv(publicEnv.PUBLIC_FIREBASE_PROJECT_ID, "PUBLIC_FIREBASE_PROJECT_ID"),
  storageBucket: readPublicEnv(publicEnv.PUBLIC_FIREBASE_STORAGE_BUCKET, "PUBLIC_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: readPublicEnv(
    publicEnv.PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    "PUBLIC_FIREBASE_MESSAGING_SENDER_ID"
  ),
  appId: readPublicEnv(publicEnv.PUBLIC_FIREBASE_APP_ID, "PUBLIC_FIREBASE_APP_ID"),
  measurementId: measurementId(),
});

const getFirebaseApp = (): FirebaseApp => {
  const existingApp = getApps()[0];
  return existingApp ?? initializeApp(firebaseConfig());
};

const gatesPass = () => {
  if (!browser) {
    debugLog("disabled outside browser");
    return false;
  }
  if (!analyticsEnabled()) {
    debugLog("disabled by PUBLIC_ANALYTICS_ENABLED");
    return false;
  }
  if (!measurementId()) {
    debugLog("disabled without measurement id");
    return false;
  }
  if (!allowedOrigin() || window.location.origin !== allowedOrigin()) {
    debugLog("disabled on non-allowed origin", window.location.origin);
    return false;
  }
  if (getPrivacyConsentRecord()?.analytics !== "granted") {
    debugLog("disabled without granted consent");
    return false;
  }
  return true;
};

export function initAnalyticsIfAllowed(): Analytics | null {
  if (analyticsInstance) {
    return analyticsInstance;
  }

  if (!gatesPass() || initializationAttempted) {
    return null;
  }

  initializationAttempted = true;

  try {
    const app = getFirebaseApp();
    analyticsInstance = initializeAnalytics(app, {
      config: { send_page_view: false },
    });
  } catch (error) {
    try {
      analyticsInstance = getAnalytics(getFirebaseApp());
    } catch (fallbackError) {
      debugLog("Firebase analytics initialization failed", { error, fallbackError });
      analyticsInstance = null;
      return null;
    }
  }

  return analyticsInstance;
}

const safeMethod = (value: unknown) => {
  const method = String(value || "")
    .trim()
    .toLowerCase();
  return ["anonymous", "google", "microsoft", "password"].includes(method) ? method : undefined;
};

const safeEnum = <T extends readonly string[]>(value: unknown, allowed: T, fallback: T[number]) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return (allowed as readonly string[]).includes(normalized) ? (normalized as T[number]) : fallback;
};

const sanitizeParams = (eventName: string, params: EventParams = {}): EventParams | null => {
  if (eventName === "logout") {
    return {};
  }

  if (eventName === "login" || eventName === "sign_up") {
    const method = safeMethod(params.method);
    return method ? { method } : {};
  }

  if (eventName === "auth_failure") {
    return {
      flow: safeEnum(params.flow, AUTH_FAILURE_FLOWS, "sign_in"),
      method: safeEnum(params.method, AUTH_FAILURE_METHODS, "unknown"),
      stage: safeEnum(params.stage, AUTH_FAILURE_STAGES, "completion"),
      reason: safeEnum(params.reason, AUTH_FAILURE_REASONS, "unknown"),
    };
  }

  if (eventName === "page_view") {
    const pageLocation = typeof params.page_location === "string" ? params.page_location : "";
    const pagePath = typeof params.page_path === "string" ? params.page_path : "";
    const pageTitle = typeof params.page_title === "string" ? params.page_title : "";
    return {
      page_location: pageLocation.split(/[?#]/)[0],
      page_path: pagePath.split(/[?#]/)[0],
      page_title: pageTitle.slice(0, 120),
    };
  }

  if (eventName === "returning_start_viewed" || eventName === "returning_start_action_clicked") {
    const urgency = String(params.urgency_band || "").trim();
    return {
      variant: safeEnum(params.variant, RETURNING_START_VARIANTS, "returning_generic"),
      recommendation_family: safeEnum(
        params.recommendation_family,
        RETURNING_START_RECOMMENDATION_FAMILIES,
        "find_next_step"
      ),
      ...(RETURNING_START_URGENCY_BANDS.includes(urgency as (typeof RETURNING_START_URGENCY_BANDS)[number])
        ? { urgency_band: urgency }
        : {}),
      destination_family: safeEnum(
        params.destination_family,
        RETURNING_START_DESTINATION_FAMILIES,
        "find_next_step"
      ),
      position: safeEnum(params.position, RETURNING_START_POSITIONS, "primary"),
    };
  }

  if (eventName === "streaming_ux_turn") {
    const boundedMs = (value: unknown) => Math.max(-1, Math.min(3_600_000, Math.round(Number(value) || 0)));
    return {
      first_backend_progress_ms: boundedMs(params.first_backend_progress_ms),
      first_cards_available_ms: boundedMs(params.first_cards_available_ms),
      preview_displayed: params.preview_displayed === true,
      preview_result_count: Math.max(0, Math.min(100, Math.round(Number(params.preview_result_count) || 0))),
      final_arrival_ms: boundedMs(params.final_arrival_ms),
      early_card_lead_ms: boundedMs(params.early_card_lead_ms),
    };
  }

  return null;
};

export function trackEvent(eventName: string, params?: EventParams) {
  const analytics = initAnalyticsIfAllowed();
  if (!analytics) {
    return false;
  }

  const safeParams = sanitizeParams(eventName, params);
  if (safeParams === null) {
    debugLog("dropped unsupported analytics event", eventName);
    return false;
  }

  logEvent(analytics, eventName, safeParams);
  return true;
}

export function trackPageView() {
  if (!browser) {
    return;
  }

  const pathname = window.location.pathname;
  trackEvent("page_view", {
    page_location: `${window.location.origin}${pathname}`,
    page_path: pathname,
    page_title: document.title,
  });
}

export function __resetAnalyticsForTests() {
  analyticsInstance = null;
  initializationAttempted = false;
  appliedConsent = null;
}
